import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface CachedData {
  overview?: any;
  users?: any[];
  payments?: any[];
  earnings?: any[];
  coupons?: any[];
  help?: any[];
  chats?: any[];
  pages?: any[];
  blogs?: any[];
  plans?: any[];
  referrals?: any;
  'profile-verification'?: any[];
  'pending-profile-changes'?: any[];
  'reported-users'?: any[];
  'role-management'?: any[];
  'wordpress-sites'?: any[];
  'wordpress-ads'?: any[];
  faq?: any[];
  'contact-settings'?: any;
  'profile-fields-config'?: any[];
  'assign-leads'?: any;
  'manager-leads'?: any;
  'leads-settings'?: any;
  'affiliate-commissions'?: any[];
  commissions?: any[];
  premium?: any[];
  'discovery-settings'?: any;
  'booster-settings'?: any;
  recharge?: any;
  lastFetchTime?: { [key: string]: number };
}

interface DataCacheContextType {
  cache: CachedData;
  updateCache: (key: string, data: any) => void;
  clearCache: (key?: string) => void;
  shouldRefresh: (key: string, ttl?: number) => boolean;
  invalidateCache: (key: string) => void;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) throw new Error('useDataCache must be used within DataCacheProvider');
  return context;
};

export function DataCacheProvider({ children }: { children: ReactNode }) {
  const [cache, setCache] = useState<CachedData>({ lastFetchTime: {} });

  const updateCache = useCallback((key: string, data: any) => {
    setCache(prev => ({
      ...prev,
      [key]: data,
      lastFetchTime: {
        ...prev.lastFetchTime,
        [key]: Date.now()
      }
    }));
  }, []);

  const clearCache = useCallback((key?: string) => {
    if (key) {
      setCache(prev => {
        const newCache = { ...prev };
        delete newCache[key as keyof CachedData];
        const newFetchTime = { ...prev.lastFetchTime };
        delete newFetchTime[key];
        return { ...newCache, lastFetchTime: newFetchTime };
      });
    } else {
      setCache({ lastFetchTime: {} });
    }
  }, []);

  const shouldRefresh = useCallback((key: string, ttl: number = 300000) => {
    const lastFetch = cache.lastFetchTime?.[key];
    if (!lastFetch) return true;
    return Date.now() - lastFetch > ttl;
  }, [cache.lastFetchTime]);

  const invalidateCache = useCallback((key: string) => {
    setCache(prev => {
      const newFetchTime = { ...prev.lastFetchTime };
      delete newFetchTime[key];
      return { ...prev, lastFetchTime: newFetchTime };
    });
  }, []);

  return (
    <DataCacheContext.Provider value={{ cache, updateCache, clearCache, shouldRefresh, invalidateCache }}>
      {children}
    </DataCacheContext.Provider>
  );
}